(ns yardwerkz.web.booking.wizard
  "Booking wizard: step coordinator with breadcrumb navigation.
   Manages current step and shared draft state."
  (:require
   [uix.core :refer [defui $ use-state use-effect]]
   [io.factorhouse.rfx.core :as rfx]
   [yardwerkz.web.store :as store]
   [yardwerkz.web.ui.layout :as layout]
   [yardwerkz.web.ui.components :as ui]
   [yardwerkz.web.ui.icons :as icons]
   ;; Step components
   [yardwerkz.web.booking.service :as service-step]
   [yardwerkz.web.booking.address :as address-step]
   [yardwerkz.web.booking.outline :as outline-step]
   [yardwerkz.web.booking.details :as details-step]
   [yardwerkz.web.booking.schedule :as schedule-step]
   [yardwerkz.web.booking.pricing :as pricing-step]
   [yardwerkz.web.booking.review :as review-step]))

;; =============================================================================
;; Steps Configuration
;; =============================================================================

(def steps
  [{:id :service  :label "Service"  :number 1}
   {:id :address  :label "Location" :number 2}
   {:id :outline  :label "Map Area" :number 3}
   {:id :details  :label "Details"  :number 4}
   {:id :schedule :label "Schedule" :number 5}
   {:id :pricing  :label "Pricing"  :number 6}
   {:id :review   :label "Review"   :number 7}])

(def step-ids (mapv :id steps))

;; =============================================================================
;; Validation
;; =============================================================================

(defn- step-valid?
  "Check if a step has sufficient data to proceed."
  [step draft]
  (case step
    :service  (some? (get-in draft [:service :id]))
    :address  (some? (get-in draft [:address :lng]))
    :outline  (seq (:geo draft))
    :details  true  ;; optional
    :schedule (and (seq (get-in draft [:schedule :date]))
                   (some? (get-in draft [:schedule :time])))
    :pricing  (some? (get-in draft [:pricing :tier]))
    :review   true
    false))

;; =============================================================================
;; Step Indicator
;; =============================================================================

(defui step-indicator [{:keys [current-step on-step-click draft]}]
  ($ :div {:class "flex items-center justify-center space-x-1 mb-8 overflow-x-auto pb-2"}
     (for [{:keys [id label number]} steps]
       (let [step-idx (.indexOf step-ids id)
             current-idx (.indexOf step-ids current-step)
             is-current (= id current-step)
             is-complete (< step-idx current-idx)
             ;; Can navigate to this step if all prior steps are valid
             can-click (or is-current is-complete
                           (every? #(step-valid? % draft)
                                   (subvec step-ids 0 step-idx)))]
         ($ :<> {:key id}
            (when (> number 1)
              ($ :div {:class (str "h-px w-6 flex-shrink-0 "
                                   (if (or is-complete is-current)
                                     "bg-green-400" "bg-gray-200"))}))
            ($ :button
               {:class (str "flex items-center space-x-1 px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-colors flex-shrink-0 "
                            (cond
                              is-current "bg-green-600 text-white"
                              is-complete "bg-green-100 text-green-700 hover:bg-green-200"
                              :else "bg-gray-100 text-gray-400"))
                :on-click (when can-click #(on-step-click id))
                :disabled (not can-click)}
               (when is-complete
                 ($ icons/Check {:size 12}))
               ($ :span label)))))))

;; =============================================================================
;; Wizard Page
;; =============================================================================

(defui wizard-page []
  (let [[current-step set-step] (use-state :service)
        draft (rfx/use-sub [::store/draft-job])
        dispatch (rfx/use-dispatch)

        current-idx (.indexOf step-ids current-step)

        go-next (when (< current-idx (dec (count steps)))
                  #(set-step (nth step-ids (inc current-idx))))
        go-back (when (> current-idx 0)
                  #(set-step (nth step-ids (dec current-idx))))

        ;; Is the current step valid enough to proceed?
        can-advance (step-valid? current-step draft)

        ;; Outline step gets full width for maximum map area
        full-width? (= current-step :outline)]

    ;; Hydrate draft from localStorage on mount
    (use-effect
     (fn []
       (when-let [saved-draft (store/load-draft)]
         (dispatch [::store/hydrate-draft saved-draft]))
       js/undefined)
     [dispatch])

    ($ layout/page-container {:full-width full-width?}
       ($ :h1 {:class "text-2xl font-bold text-gray-900 mb-4"} "Book a Service")
       ($ step-indicator {:current-step current-step
                          :on-step-click set-step
                          :draft draft})

       ;; Step content
       (case current-step
         :service  ($ service-step/service-step {:on-next go-next})
         :address  ($ address-step/address-step {:on-next go-next})
         :outline  ($ outline-step/outline-step {:on-next go-next :on-back go-back})
         :details  ($ details-step/details-step {:on-next go-next :on-back go-back})
         :schedule ($ schedule-step/schedule-step {:on-next go-next :on-back go-back})
         :pricing  ($ pricing-step/pricing-step {:on-next go-next :on-back go-back})
         :review   ($ review-step/review-step {:on-back go-back})
         ($ service-step/service-step {:on-next go-next}))

       ;; Navigation footer (except review which has its own)
       (when (not= current-step :review)
         ($ :div {:class "flex items-center justify-between mt-8 pt-4 border-t border-gray-200"}
            (if go-back
              ($ ui/button {:variant :secondary :on-click go-back}
                 ($ :span {:class "flex items-center space-x-1"}
                    ($ icons/ChevronLeft {:size 16})
                    ($ :span "Back")))
              ($ :div)) ;; spacer

            (when go-next
              ($ ui/button {:on-click go-next
                            :disabled (not can-advance)}
                 ($ :span {:class "flex items-center space-x-1"}
                    ($ :span "Next")
                    ($ icons/ChevronRight {:size 16})))))))))
